// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultChartPropsDefinedSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  createDropDownSchema,
  createPopupSettingSchema,
  createCheckBoxSchema,
  createSelectSchema,
  createNumberSchema,
  createColorPaletteSettingSchema,
  createOrientationSettingsSchema
} from 'share/utils/Izenda.PropsEditorGuiSchemaFunctions';

import ChartDefaultConfig from 'logic/data/Izenda.ChartDefaultConfig';
import { getAll } from 'share/factories/Izenda.ReportPartStyleFactories';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { DefaultChartType, CHART_TYPES, CHART_SCHEMA_NAMES } from 'logic/data/Izenda.ReportPartChartConstants';
import localizer from 'share/localization/Izenda.Localizer';

import ChartBorderSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartBorderSettings';
import ChartBackgroundColorSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartBackgroundColorSettings';
import ChartDataRefreshSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.DataRefreshSetting';
import ChartXYPlaneSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartXYPlaneSettings';
import ChartGridLineSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartGridLinesSettings';
import ChartLegendSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartLegendSettings';
import ChartThresholdSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ThresholdSettings';
import ChartRegressionLineSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartRegressionLineSettings';

const { CHART_KEY, GROUPS, FIELDS } = CHART_SCHEMA_NAMES;

const getChartTypeOptions = () =>
  getAll(REPORT_PART_TYPE.Chart).map(({ label, style, previewCSS }) => ({
    label,
    value: style,
    className: previewCSS
  }));

/**
 * The common chart properties GUI schema that shares to all chart type.
 */
export default {
  schemaKey: CHART_KEY,
  groups: {
    [GROUPS.GENERAL_INFO_GROUP]: {
      title: () => localizer.getText('COMMON_LABEL_GENERAL_INFO'),
      fields: {
        [FIELDS.GENERAL_INFO_CHART_TYPE]: {
          factory: createDropDownSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_TYPE'),
          value: 'getChartType',
          props: {
            defaultValue: DefaultChartType,
            get options() {
              return getChartTypeOptions();
            }
          }
        }
      }
    },
    [GROUPS.CHART_GROUP]: {
      title: () => localizer.getText('COMMON_LABEL_CHART'),
      isHidden: 'isNotBuild',
      hiddenByState: true,
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.CHART_BORDER]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_BORDER'),
          value: 'getBorder',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_BORDER_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingBorder',
            fnResetDefaultValue: 'fnResetBorderDefaultValue',
            loadValue: 'getBorder'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_BORDER_SETTINGS');
            },
            popupContent: ChartBorderSettings
          }
        },
        [FIELDS.CHART_BGCOLOR]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COLOR_STYLING_BACKGROUND_TOOLTIP'),
          value: 'getBackgroundColor',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_BACKGROUND_COLOR_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingBackgroundColor',
            fnResetDefaultValue: 'fnResetBackgroundColorDefaultValue',
            loadValue: 'getBackgroundColor'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_BACKGROUND_COLOR_SETTINGS');
            },
            popupContent: ChartBackgroundColorSettings
          }
        },
        [FIELDS.CHART_XY_PLANE]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_XY_PLANE'),
          isHidden: 'hideByChartType',
          value: 'getXYPlaneSettings',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_XY_PLANE_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            reportPartDetails: 'getReportPartDetails',
            fieldStore: 'getFieldStore',
            hadSetting: 'hadSettingXYPlane',
            fnResetDefaultValue: 'fnResetXYPlaneDefaultValue',
            loadValue: 'getXYPlaneSettings'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_XY_PLANE_SETTINGS');
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_XY_PLANE_SETTINGS');
            },
            popupContent: ChartXYPlaneSettings
          }
        },
        [FIELDS.CHART_GRID_LINE]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_GRID_LINES'),
          isHidden: 'hideByChartType',
          value: 'getGridLines',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_GRID_LINES_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hasSettingGridLine',
            fnResetDefaultValue: 'resetDefaultGridLine',
            loadValue: 'getGridLines'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_GRID_LINES_SETTINGS');
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_GRID_LINES_SETTINGS');
            },
            popupContent: ChartGridLineSettings
          }
        },
        [FIELDS.CHART_COLOR_THEME]: {
          factory: createColorPaletteSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_COLOR_THEME'),
          value: 'getColorTheme',
          props: {
            get selectionPopupTitle() {
              return localizer.getText('COLORTHEME_POPUP_HEADER');
            },
            get defaultColorThemeLabel() {
              return localizer.getText('COLORPALETTE_NOTHEME');
            },
            displaySettingStatus: true,
            needMaskDefault: false,
            defaultColorPalette: null
          }
        },
        [FIELDS.CHART_MULTI_COLOR]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_COLOR'),
          info: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_COLOR_INFO'),
          isHidden: 'hideByChartTypeAndMetrics',
          value: 'getValueByKey',
          props: {
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_COLOR_SETTINGS');
            }
          }
        }
        ,
        [FIELDS.CHART_MULTI_LEVEL]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_LEVEL'),
          info: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_LEVEL_INFO'),
          isHidden: 'hideByChartType',
          value: 'getValueByKey',
          props: {
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_MULTI_COLOR_SETTINGS');
            }
          }
        }
      }
    },
    [GROUPS.LABEL_GROUP]: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LABELS'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.LABEL_ORIENTATION]: {
          factory: createOrientationSettingsSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DATA_LABELS'),
          isHidden: 'hideByChartType',
          value: 'getValueByKey'
        },
        [FIELDS.LABEL_ORIENTATION_X]: {
          factory: createOrientationSettingsSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_X_LABELS'),
          isHidden: 'hideByChartType',
          value: 'getLabelOrientationX'
        },
        [FIELDS.LABEL_ORIENTATION_Y]: {
          factory: createOrientationSettingsSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_Y_LABELS'),
          isHidden: 'hideByChartType',
          value: 'getLabelOrientationY'
        },
        [FIELDS.LABEL_HOVER]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_HOVER_LABELS'),
          value: 'getValueByKey'
        }
      }
    },
    [GROUPS.LEGENDS_GROUP]: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LEGENDS'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.LEGENDS_SETTINGS]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SETTINGS'),
          value: 'getLegendSettings',
          isHidden: 'hideByChartType',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_LEGEND_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            chartType: 'getChartType',
            hadSetting: 'hadSettingLegend',
            fnResetDefaultValue: 'fnResetLegendDefaultValue',
            loadValue: 'getLegendSettings'
          },
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LEGEND_SETTINGS');
            },
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LEGEND_SETTINGS');
            },
            popupContent: ChartLegendSettings
          }
        }
      }
    },
    [GROUPS.GROUPING_GROUP]: {
      title: () => localizer.getText('COMMON_LABEL_GROUPING'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.GROUPING_USE_SEPARATOR]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('COMMON_LABEL_USE_SEPARATOR'),
          value: 'getUseSeparator',
          isHidden: 'hideByChartType'
        },
        [FIELDS.GROUPING_STACKING]: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_STACKED'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            blankOption: true,
            get options() {
              return ChartDefaultConfig.getOptions('STACK_TYPE_CHART');
            }
          }
        },
        [FIELDS.GROUPING_BOTTOM_X_PERCENT]: {
          factory: createNumberSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_BOTTOM_PERCENT_GROUPED_TO_OTHER'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            min: 0,
            max: 100
          }
        }
      }
    },
    [GROUPS.DATA_GROUP]: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DATA'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.DATA_SHOW_PERCENTAGE]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SHOW_PERCENTAGE'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.DATA_VALUE_LABEL]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SHOW_VALUE_LABELS'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.DATA_SLICE_LABEL]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SHOW_SLICE_LABELS'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.DATA_RANGE]: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_RANGE'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            blankOption: true,
            get options() {
              return ChartDefaultConfig.getOptions('RANGE');
            },
            filterOptions: 'filterRangeOptions'
          }
        },
        [FIELDS.DATA_THRESHOLDS]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_THRESHOLDS'),
          value: 'getThresholdSettings',
          isHidden: 'hideByChartType',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_THRESHOLDS_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            reportPartDetails: 'reportPartDetails',
            fieldStore: 'getFieldStore',
            hadSetting: 'hadSettingThreshold',
            fnResetDefaultValue: 'fnResetThresholdDefaultValue',
            loadValue: 'getThresholdSettings'
          },
          staticProps: {
            containerClassName: 'threshold-config',
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_THRESHOLDS_SETTINGS');
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_THRESHOLDS_SETTINGS');
            },
            popupContent: ChartThresholdSettings,
            popupProps: {
              type: 'none',
              autoClose: false,
              sizeClass: 'modal-lg',
              containerClass: 'izenda-ThresholdSettings izenda-ChartSettingsPopup'
            }
          }
        },
        [FIELDS.DATA_REGRESSION_LINE]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_REGRESSION_LINE'),
          value: 'getRegressionLineSettings',
          isHidden: 'hideByChartType',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_REGRESSION_LINE_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            reportPartDetails: 'reportPartDetails',
            fieldStore: 'getFieldStore',
            hadSetting: 'hadSettingRegressionLine',
            fnResetDefaultValue: 'fnResetRegressionLineDefaultValue',
            loadValue: 'getRegressionLineSettings',
            warningCondition: 'hadSeparators',
            warningAction: 'removeUseSeparator'
          },
          staticProps: {
            containerClassName: '',
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_REGRESSION_LINE_SETTINGS');
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_REGRESSION_LINE_SETTINGS');
            },
            popupContent: ChartRegressionLineSettings,
            popupProps: {
              type: 'none',
              autoClose: false,
              sizeClass: 'modal-lg',
              containerClass: 'izenda-RegressionLineSettings izenda-ChartSettingsPopup'
            },
            get warningMessage() {
              return localizer.getText('REGRESSION_LINE_POPUP_WARNING');
            }
          }
        }
      }
    },
    [GROUPS.VIEW_GROUP]: {
      title: () => localizer.getText('MODEL_LABEL_VIEW'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.VIEW_ZOOM]: {
          factory: createSelectSchema,
          title: () => localizer.getText('SCHEMA_ZOOM'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            blankOption: true,
            get options() {
              return ChartDefaultConfig.getOptions('ZOOM_TYPE');
            }
          }
        },
        [FIELDS.VIEW_INVERTED]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('COMMON_LABEL_INVERTED'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.VIEW_STEP]: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_STEP'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            blankOption: true,
            get options() {
              return ChartDefaultConfig.getOptions('STEP_TYPE');
            }
          }
        },
        [FIELDS.VIEW_SPLINE]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SPLINE'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.VIEW_PIE_CHART_STYLE]: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_PIE_CHART_STYLE'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            get options() {
              return ChartDefaultConfig.getOptions('PIE_CHART_STYPE');
            }
          }
        },
        [FIELDS.VIEW_DATA_REFRESH_INTERVAL]: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING'),
          value: 'getValueByKey',
          isHidden: 'isNotBuild',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Chart;
            },
            get title() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            hiddenByState: true,
            hadSetting: 'hadSettingRefreshInterval',
            allChartProps: 'getAllChartProps',
            fnResetDefaultValue: 'fnResetRefreshIntervalDefaultValue',
            loadValue: 'getDataRefreshInterval'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            popupContent: ChartDataRefreshSettings,
            popupProps: {
              type: 'none',
              autoClose: false
            }
          }
        },
        [FIELDS.VIEW_DATA_USE_PAGINATION]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('COMMON_LABEL_USE_PAGINATION'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType'
        },
        [FIELDS.VIEW_ITEMS_PER_ROW]: {
          factory: createNumberSchema,
          title: () => localizer.getText('COMMON_LABEL_ITEM_PER_ROW'),
          value: 'getValueByKey',
          isHidden: 'hideByChartType',
          props: {
            min: 1,
            max: 20,
            isDecimal: false
          }
        },
        [FIELDS.VIEW_SINGLE_YAXIS]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SINGLE_YAXIS'),
          value: 'getValueByKey',
          isHidden: 'doesHideSingleYAxis'
        }
      }
    },
    [GROUPS.PRINTING_GROUP]: {
      title: () => localizer.getText('COMMON_LABEL_PRINTINGS'),
      isHidden: 'doesHidePageBreakAfterSeparator',
      className: 'getClassNameByChartStatus',
      fields: {
        [FIELDS.PRINTING_PAGE_BREAK_AFTER_SEPARATOR]: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_PAGE_BREAK_AFTER_SEPARATOR'),
          value: 'getValueByKey'
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DefaultChartPropsDefinedSchema.js